import React from "react";

// ErrorBoundary — single safety net so a render-time JS crash inside
// any route can't black-screen the entire app (the failure mode that
// stranded the iOS TestFlight build on /map in v1.0 build 1).
//
// Two-mode rendering:
//   • `scope="app"` (default) — full-page fallback with branding +
//     "Go home" button. Used by AppShell as the outermost boundary.
//   • `scope="page"` — compact in-place fallback for individual pages
//     (e.g. MapPage) so the rest of the chrome (header, menu, FAB)
//     stays usable when only one section explodes.
//
// Errors are forwarded to Sentry via lib/sentry's existing global
// handler — no extra wiring needed here. We also log to console so the
// Safari Web Inspector workflow (iPhone → Mac → Develop menu) surfaces
// a clear stack the moment the issue reproduces.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    try {
      if (typeof window !== "undefined" && window.Sentry) {
        window.Sentry.captureException(error, {
          extra: { componentStack: info?.componentStack },
        });
      }
    } catch (_) { /* noop — Sentry might not be initialised */ }
  }

  handleReset = () => {
    // Go home + force a fresh render. window.location is intentional
    // (not navigate()) so any leaked state — Leaflet singletons,
    // service worker caches, runaway intervals — is cleared.
    try {
      window.location.href = "/";
    } catch (_) { /* noop */ }
  };

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const scope = this.props.scope || "app";
    const message = error?.message || "Unknown error";

    if (scope === "page") {
      return (
        <div
          className="w-full h-full min-h-[50vh] flex items-center justify-center p-6 bg-black"
          data-testid="error-boundary-page"
        >
          <div className="max-w-sm w-full bg-gray-900/80 border border-rose-500/40 rounded-lg p-5 text-center">
            <div className="text-rose-300 text-xs uppercase tracking-wider font-bold mb-2">
              This section hit a snag
            </div>
            <p className="text-gray-200 text-sm mb-4 break-words">{message}</p>
            <button
              type="button"
              onClick={this.handleReset}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2 rounded transition-colors"
              data-testid="error-boundary-page-home-btn"
            >
              Back to feed
            </button>
          </div>
        </div>
      );
    }

    // scope === "app" — full-screen recovery surface.
    return (
      <div
        className="fixed inset-0 z-[9999] bg-black flex items-center justify-center p-6"
        data-testid="error-boundary-app"
      >
        <div className="max-w-md w-full bg-gray-900 border border-emerald-500/30 rounded-xl p-6 shadow-2xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-emerald-400 text-2xl">👁️</span>
            <span className="text-white font-bold tracking-wide">GlobalEyes.TV</span>
          </div>
          <h1 className="text-white text-lg font-semibold mb-1">Something broke.</h1>
          <p className="text-gray-400 text-sm mb-4">
            We caught the crash so the app stays usable. Try going home —
            most of the time that clears it.
          </p>
          <pre
            className="bg-black/60 border border-gray-800 rounded p-2 text-rose-300 text-[11px] leading-tight overflow-auto max-h-32 mb-4"
            data-testid="error-boundary-app-message"
          >
            {message}
          </pre>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2 rounded transition-colors"
              data-testid="error-boundary-app-home-btn"
            >
              Go home
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="flex-1 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-sm font-medium px-4 py-2 rounded transition-colors"
              data-testid="error-boundary-app-reload-btn"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
